import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const HealthPackages = ({ isAdmin = false, onEditClick }) => {
  const [packagesData, setPackagesData] = useState({
    heading: "",
    subheading: "",
    packages: [],
    buttonText: "",
    buttonLink: ""
  });
  const [isLoading, setIsLoading] = useState(true);

  const COLORS = {
    primary: '#0760ad',
    secondary: '#098a48',
    tertiary: '#118a94',
    green: '#339166',
    white: '#FFFFFF',
    lightGray: '#f8fafc',
    darkText: '#1a365d'
  };

  useEffect(() => {
    const fetchPackagesData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/home/health-packages/`);
        if (response.ok) {
          const data = await response.json();
          setPackagesData({
            heading: data.heading || "Comprehensive Health Packages",
            subheading: data.subheading || "Tailored healthcare solutions for individuals, families, and organizations.",
            packages: data.packages || [],
            buttonText: data.buttonText || "View All Packages",
            buttonLink: data.buttonLink || "",
          });
        }
      } catch (error) {
        console.error('Error fetching health packages data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackagesData();
  }, []);

  const PackageCard = ({ pkg, index }) => {
    const gradients = [
      `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.tertiary} 100%)`,
      `linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.green} 100%)`,
      `linear-gradient(135deg, ${COLORS.tertiary} 0%, ${COLORS.primary} 100%)`
    ];

    return (
      <motion.div
        whileHover={{ y: -8 }}
        className="h-full"
      >
        <div className="text-justify h-full bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-all">
          <div 
            className="h-3 w-full"
            style={{ background: gradients[index % gradients.length] }}
          ></div>
          
          <div className="p-6">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4 overflow-hidden"
                style={{ 
                  background: gradients[index % gradients.length],
                }}
              >
                <img 
                  src={pkg.icon} 
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/32?text=Icon';
                  }}
                />
              </div>
           
            
            <h3 className="text-xl font-bold mb-3" style={{ color: COLORS.darkText }}>
              {pkg.title}
            </h3>
            
            <p className="text-gray-600 mb-5">{pkg.description}</p>
            
            <ul className="mb-6 space-y-2">
              {pkg.features?.map((feature, i) => (
                <li key={i} className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-2" style={{ 
                    background: gradients[index % gradients.length] 
                  }}></div>
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
         
          </div>
        </div>
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 flex justify-center">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white relative">
      {isAdmin && (
        <button 
          onClick={onEditClick}
          className="absolute top-4 right-4 btn btn-sm btn-primary z-10"
        >
          Edit Section
        </button>
      )}
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ 
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.tertiary})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {packagesData.heading}
          </h2>
          
          <div className="w-24 h-1 mx-auto mb-6 rounded-full" style={{ 
            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` 
          }}></div>
          
          <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.darkText }}>
            {packagesData.subheading}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {packagesData.packages.map((pkg, index) => (
            <PackageCard key={index} pkg={pkg} index={index} />
          ))}
        </div>

        {packagesData.buttonText && (
          <div className="text-center mt-12">
            <a href={packagesData.buttonLink} className="inline-block">
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className="px-8 py-3 rounded-lg font-medium text-white shadow-lg"
    style={{
      background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.green})`,
      boxShadow: `0 4px 20px ${COLORS.secondary}40`
    }}
  >
    {packagesData.buttonText}
  </motion.button>
</a>

          </div>
        )}
      </div>
    </section>
  );
};

export default HealthPackages;